import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, Icon } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import {
  getStagesFor,
  STAGE_DONE,
  STAGE_NA,
  getStageStatusLabel,
  getTaskStages,
  getStagesTiming,
  getStagesAgeTone,
  getNextStageKey,
  countStagesMarked,
  getStageNotes,
  getStageScope,
} from '~/_domains/societario/helpers';
import { useSocietarioStages } from '~/_domains/societario/stagesConfig';

// Faixa de etapas do card do painel.
//
// O âmbar da etapa pendente parecia discreto num card isolado, mas a coluna
// inteira é feita de processos que mal começaram: o quadro virava uma parede
// amarela e o que já tinha andado se perdia no meio. Pendente agora é cinza —
// ausência de sinal, não alerta — e só o que foi resolvido ganha cor.
const TONES = {
  pending: { background: '#f5f5f5', color: '#8c8c8c' },
  done: { background: '#52c41a', color: '#fff' },
  na: { background: '#ffccc7', color: '#a8071a' },
};

// Cronômetro: cinza enquanto é cedo, âmbar a partir de 15 dias, vermelho a
// partir de 30, verde quando o processo fecha.
const TIMER_TONES = {
  fresh: { border: '#e8e8e8', background: '#fafafa', color: '#8c8c8c' },
  warn: { border: '#ffe58f', background: '#fffbe6', color: '#d48806' },
  late: { border: '#ffa39e', background: '#fff1f0', color: '#cf1322' },
  finished: { border: '#b7eb8f', background: '#f6ffed', color: '#389e0d' },
};

const getTone = (status) => {
  if (status === STAGE_DONE) return TONES.done;
  if (status === STAGE_NA) return TONES.na;
  return TONES.pending;
};

const Strip = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const Track = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  border-radius: 3px;
  overflow: hidden;
`;

const Segment = styled.span`
  position: relative;
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 17px;
  padding: 0 2px;
  overflow: hidden;
  white-space: nowrap;
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  /* Não aplicável fica riscada: o recado não depende de enxergar a cor. */
  text-decoration: ${(props) => (props.struck ? 'line-through' : 'none')};
  /* Etapa da vez ganha um traço embaixo — mostra até onde o processo andou. */
  box-shadow: ${(props) => (props.current ? 'inset 0 -2px 0 #1890ff' : 'none')};

  & + & {
    margin-left: 1px;
  }

  /* Pontinho azul no canto: a etapa tem observação (aparece no tooltip). */
  &::after {
    content: '';
    display: ${(props) => (props.noted ? 'block' : 'none')};
    position: absolute;
    top: 2px;
    right: 2px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #1890ff;
  }
`;

const Timer = styled.span`
  flex-shrink: 0;
  margin-left: 8px;
  padding: 0 7px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
  border: 1px solid ${(props) => props.tone.border};
  background: ${(props) => props.tone.background};
  color: ${(props) => props.tone.color};
`;

export const StagesStrip = ({ item }) => {
  useSocietarioStages();
  const scope = getStageScope(item);
  const list = getStagesFor(scope);
  const stages = getTaskStages(item);
  const notes = getStageNotes(item);
  const timing = getStagesTiming(stages, scope);
  const nextKey = getNextStageKey(stages, scope);
  const usersById = useSelector((state) => state.usersById);
  const timerTone = TIMER_TONES[getStagesAgeTone(timing)] || TIMER_TONES.fresh;

  const describe = (stage, current) => {
    const note = notes[stage.key];
    const user = current && current.by && usersById ? usersById[current.by] : null;

    return (
      <span>
        <strong>{stage.label}</strong>
        <br />
        {getStageStatusLabel(current ? current.status : null)}
        {current && current.at ? ' em ' + moment(current.at).format('DD/MM/YYYY') : ''}
        {user && user.name ? ' por ' + user.name : ''}
        {stage.key === nextKey ? ' — é a etapa da vez' : ''}
        {note && note.text && (
          <span style={{ display: 'block', marginTop: 4 }}>
            <Icon type="message" style={{ marginRight: 4 }} />
            {note.text}
          </span>
        )}
      </span>
    );
  };

  const timerTitle = () => {
    const resolvidas = countStagesMarked(stages, scope) + ' de ' + list.length + ' etapas resolvidas';

    if (!timing.running) {
      return 'Processo concluído em ' + timing.days + ' dia(s) · ' + resolvidas;
    }

    return 'Em andamento há ' + timing.days + ' dia(s), desde a primeira etapa marcada · ' + resolvidas;
  };

  // Sem a lista do escritório ainda carregada, nada a desenhar (ver helpers).
  if (list.length === 0) {
    return null;
  }

  return (
    <Strip>
      <Track>
        {list.map((stage) => {
          const current = stages[stage.key];
          const status = current ? current.status : null;
          const tone = getTone(status);

          return (
            <Tooltip key={stage.key} title={describe(stage, current)}>
              <Segment
                background={tone.background}
                color={tone.color}
                struck={status === STAGE_NA ? 1 : 0}
                current={stage.key === nextKey ? 1 : 0}
                noted={notes[stage.key] && notes[stage.key].text ? 1 : 0}
              >
                {stage.short}
              </Segment>
            </Tooltip>
          );
        })}
      </Track>
      {timing && (
        <Tooltip title={timerTitle()}>
          <Timer tone={timerTone}>
            <Icon type="clock-circle" style={{ marginRight: 3, fontSize: 10 }} />
            {timing.days}d
          </Timer>
        </Tooltip>
      )}
    </Strip>
  );
};

export default StagesStrip;
